import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { createPortal } from 'react-dom';

import { restoreFocus } from '@/shared/lib/restore-focus';

import styles from './Popover.module.css';

export interface PopoverTriggerProps {
  ref: RefObject<HTMLButtonElement | null>;
  'aria-haspopup': 'dialog';
  'aria-expanded': boolean;
  'aria-controls': string | undefined;
  onClick: () => void;
}

interface Props {
  /** Accessible name of the panel. */
  label: string;
  /** Renders the trigger button; spread `props` onto it. */
  trigger: (props: PopoverTriggerProps, open: boolean) => ReactNode;
  /** Panel content; `close` closes it and returns focus to the trigger. */
  children: ReactNode | ((close: () => void) => ReactNode);
  /** Preferred width in px (never wider than the viewport). */
  width?: number;
  /** Align the panel's right edge with the trigger instead of the left one. */
  alignEnd?: boolean;
  /** Extra class for the wrapper element (layout hooks, e.g. a split button's segment). */
  className?: string;
  /** Controlled mode (optional): the caller can open it, e.g. from a keyboard shortcut. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * An anchored panel for menus and pickers. `position: fixed`, placed from the trigger's rectangle:
 * it floats over the page, stays inside the viewport (below the trigger when it fits, else above,
 * with its own scrolling), closes on Escape (focus returns to the trigger) and on an outside press.
 * Every control inside is an ordinary focusable element.
 */
export function Popover({
  label,
  trigger,
  children,
  width = 360,
  alignEnd = false,
  className,
  open: controlledOpen,
  onOpenChange,
}: Props) {
  const [innerOpen, setInnerOpen] = useState(false);
  const open = controlledOpen ?? innerOpen;
  const setOpen = useCallback(
    (value: boolean) => {
      setInnerOpen(value);
      onOpenChange?.(value);
    },
    [onOpenChange],
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const [placement, setPlacement] = useState<CSSProperties>({});
  const [layer, setLayer] = useState<Element | null>(null);

  const [closes, setCloses] = useState(0);
  const close = useCallback(() => {
    setOpen(false);
    setCloses((count) => count + 1);
  }, [setOpen]);
  useEffect(() => {
    // preventScroll: giving focus back must never scroll the page or a grid behind the popover.
    if (closes > 0 && buttonRef.current) restoreFocus(buttonRef.current);
  }, [closes]);

  useEffect(() => {
    if (!open) return;
    // Out of every clipping / stacking ancestor of the trigger (a header with a backdrop-filter,
    // a scrolling board). Inside a modal <dialog> it must stay in the dialog: only the dialog's own
    // subtree is above the rest in the browser top layer.
    // A popover inside another popover's panel opens inside it, so a press on the inner one is not
    // an outside press for the outer one.
    setLayer(rootRef.current?.closest('dialog, [data-popover-panel]') ?? document.body);
    const place = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const margin = 8;
      const panelWidth = Math.min(width, window.innerWidth - 2 * margin);
      const below = window.innerHeight - rect.bottom - margin;
      const above = rect.top - margin;
      const useAbove = below < 260 && above > below;
      const room = Math.max(140, useAbove ? above : below);
      const preferred = alignEnd ? rect.right - panelWidth : rect.left;
      setPlacement({
        left: Math.max(margin, Math.min(preferred, window.innerWidth - panelWidth - margin)),
        width: panelWidth,
        maxHeight: room,
        ...(useAbove ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node) {
        if (rootRef.current?.contains(event.target)) return;
        if (panelRef.current?.contains(event.target)) return;
      }
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open, width, alignEnd, setOpen]);

  return (
    <div
      ref={rootRef}
      className={[styles.root, className].filter(Boolean).join(' ')}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.preventDefault();
          event.stopPropagation(); // not the dialog / drawer behind
          close();
        }
      }}
    >
      {trigger(
        {
          ref: buttonRef,
          'aria-haspopup': 'dialog',
          'aria-expanded': open,
          'aria-controls': open ? panelId : undefined,
          onClick: () => {
            setOpen(!open);
          },
        },
        open,
      )}
      {open &&
        layer &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            data-popover-panel=""
            className={styles.panel}
            role="dialog"
            aria-label={label}
            style={placement}
          >
            {typeof children === 'function' ? children(close) : children}
          </div>,
          layer,
        )}
    </div>
  );
}
